import React, { useMemo } from "react";
import { Button } from "react-aria-components";
import { PencilIcon, DocumentIcon } from "@heroicons/react/24/outline";
import { useFormContext } from "react-hook-form";

// @ts-ignore
import LoaderCircle from "svg/loader-circle.svg";
import { formatFileSize } from "helpers/files";
import { formatPhoneNumber } from "helpers/phone";
import ErrorMessage from "components/ErrorMessage";
import { FormConfig, FieldConfig } from "./types";
import { getRelevantFieldRefs } from "./logic";
import { useFormConfig } from "./hooks";

interface ReviewScreenProps {
  formConfig: FormConfig;
  onEditField: (fieldRef: string) => void;
  onSubmit: () => void;
  submissionStatus?: "idle" | "submitting" | "success" | "error";
}

// Helper component for consistent "not answered" styling
const NotAnsweredText = () => (
  <span className="italic text-gray-400">Not answered</span>
);

const ReviewScreen: React.FC<ReviewScreenProps> = ({
  formConfig,
  onEditField,
  onSubmit,
  submissionStatus = "idle",
}) => {
  const { getValues, formState } = useFormContext();
  const { context } = useFormConfig();
  const formData = getValues();
  const { errors } = formState;

  // Check if form data has actually loaded from localStorage
  const hasFormData = React.useMemo(() => {
    const values = Object.values(formData);
    return (
      values.length > 0 &&
      values.some((value) => {
        if (value === null || value === undefined || value === "") {
          return false;
        }
        if (
          typeof value === "object" &&
          Object.values(value).every((v) => !v)
        ) {
          return false;
        }
        return true;
      })
    );
  }, [formData]);

  // Track if we've ever had form data (to distinguish reload vs fresh form)
  const [hasEverHadData, setHasEverHadData] = React.useState(false);

  // Get the relevant fields that were actually shown to the user based on logic jumps
  const relevantFields = useMemo(() => {
    const relevantFieldRefs = getRelevantFieldRefs(
      formConfig,
      context,
      formData,
    );

    return relevantFieldRefs
      .map((ref) => formConfig.fields.find((field) => field.ref === ref))
      .filter((field): field is FieldConfig => field !== undefined);
  }, [formConfig, context, formData]);

  // Check if field is valid (has value if required)
  const isFieldValid = React.useCallback(
    (field: FieldConfig) => {
      const value = formData[field.ref];

      if (field.validations?.required) {
        // For yes_no fields, false is a valid answer
        if (field.type === "yes_no") {
          if (value === undefined || value === null || value === "") {
            return false;
          }
        } else if (field.type === "number") {
          // For number fields, 0 is a valid value
          if (value === undefined || value === null || value === "") {
            return false;
          }
        } else if (
          !value ||
          (typeof value === "object" && Object.values(value).every((v) => !v))
        ) {
          return false;
        }
      }

      return !errors[field.ref];
    },
    [formData, errors],
  );

  React.useEffect(() => {
    if (hasFormData && !hasEverHadData) {
      setHasEverHadData(true);
    }
  }, [hasFormData, hasEverHadData]);

  // Give fresh forms a reasonable time to show validation
  React.useEffect(() => {
    if (!hasFormData) {
      const timer = setTimeout(() => {
        setHasEverHadData(true);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [hasFormData]);

  // Determine if we're still loading data from localStorage
  const isDataLoading = !hasFormData && !hasEverHadData;

  const hasInvalidFields = useMemo(
    () =>
      !isDataLoading && relevantFields.some((field) => !isFieldValid(field)),
    [isDataLoading, relevantFields, isFieldValid],
  );

  const renderFieldValue = (field: FieldConfig) => {
    // Show loading state for individual field values
    if (isDataLoading) {
      return (
        <div className="mt-4 animate-pulse">
          <div className="h-6 w-3/5 rounded bg-gray-200"></div>
        </div>
      );
    }

    const value = formData[field.ref];

    // Handle falsy values properly - 0 and false are valid answers
    if (value === undefined || value === null || value === "") {
      return <NotAnsweredText />;
    }

    // Format the value based on field type
    switch (field.type) {
      case "full_name": {
        const { first_name, middle_name, last_name, suffix } = value;
        const nameParts = [first_name, middle_name, last_name, suffix].filter(
          Boolean,
        );
        return (
          nameParts.join(" ") || (
            <span className="text-gray-400">Not answered</span>
          )
        );
      }

      case "location": {
        const { address_line_1, address_line_2, city, state, zip } = value;
        return (
          <div>
            {address_line_1 && <div>{address_line_1}</div>}
            {address_line_2 && <div>{address_line_2}</div>}
            {city && state && zip && (
              <div>
                {city}, {state} {zip}
              </div>
            )}
          </div>
        );
      }

      case "yes_no":
        return value ? "Yes" : "No";

      case "choices":
        if (
          field.properties?.allow_multiple_selection &&
          Array.isArray(value)
        ) {
          const labels = value.map(
            (ref) =>
              field.properties?.choices?.find((c) => c.ref === ref)?.label ||
              ref,
          );
          return labels.join(", ");
        } else {
          const choice = field.properties?.choices?.find(
            (c) => c.ref === value,
          );
          return choice?.label || value;
        }

      case "dropdown": {
        const option = field.properties?.choices?.find((c) => c.ref === value);
        return option?.label || value || <NotAnsweredText />;
      }

      case "attachment": {
        if (!value) return <NotAnsweredText />;

        return (
          <div className="space-y-2">
            <div className="flex flex-col space-y-2">
              {value.preview_blob ? (
                <img
                  src={value.preview_blob}
                  alt="Attachment preview"
                  className="h-16 w-16 rounded border object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded border bg-gray-50">
                  <DocumentIcon className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div>
                <div className="text-sm font-medium">
                  {value.file_name}
                  {value.file_size && (
                    <>
                      <span className="mx-2 text-gray-400">-</span>
                      <span className="text-gray-600">
                        {formatFileSize(value.file_size)}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      case "phone_number":
        return formatPhoneNumber(value);

      case "date":
      case "email":
      case "ssn":
      case "short_text":
      case "long_text":
      case "number":
      default:
        return String(value);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-soil">Review Your Answers</h2>
        <p className="mt-2 text-gray-600">
          Please review your information before submitting. Click the edit
          button next to any answer to make changes.
        </p>
        {hasInvalidFields && (
          <ErrorMessage className="mt-4">
            Some required fields are missing or invalid. Please complete them
            before submitting.
          </ErrorMessage>
        )}
      </div>

      <div className="space-y-4">
        {relevantFields.map((field) => {
          const isValid = isDataLoading || isFieldValid(field);
          const value = formData[field.ref];
          const hasValue =
            field.type === "yes_no" || field.type === "number"
              ? value !== undefined && value !== null && value !== ""
              : value &&
                (typeof value !== "object" ||
                  Object.values(value).some((v) => v));

          return (
            <div
              key={field.ref}
              className={`rounded-lg border bg-white p-4 transition-shadow hover:shadow-sm ${
                !isValid ? "border-red-300 bg-red-50" : "border-gray-200"
              }`}
              aria-invalid={!isValid}
              aria-describedby={!isValid ? `${field.ref}-error` : undefined}
            >
              {/* Mobile-first layout: stack vertically on 3xs screens, horizontal from 2xs and up */}
              <div className="flex flex-col gap-3 2xs:flex-row 2xs:items-start 2xs:justify-between">
                <div className="min-w-0 flex-1">
                  {/* Field title with better mobile layout */}
                  <div className="space-y-1">
                    <h3 className="break-words font-semibold leading-tight text-soil">
                      {field.title}
                    </h3>
                    {!isDataLoading &&
                      field.validations?.required &&
                      !hasValue && (
                        <span
                          id={`${field.ref}-error`}
                          className="inline-block text-sm font-normal text-red-600"
                        >
                          (Required)
                        </span>
                      )}
                  </div>
                  <div className="ph-no-capture mt-2 text-gray-700">
                    {renderFieldValue(field)}
                  </div>
                </div>
                {/* Edit button: full width on 3xs, compact from 2xs and up */}
                <div className="shrink-0">
                  <Button
                    onPress={() => onEditField(field.ref)}
                    className="flex w-full items-center justify-center gap-1 rounded-md bg-oat-100 px-4 py-2 text-sm font-medium text-soil transition-colors hover:bg-oat-200 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-cantelope 2xs:w-auto 2xs:px-3 2xs:py-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex flex-col justify-end gap-4 xs:flex-row xs:items-start xs:gap-6">
        {hasInvalidFields && (
          <ErrorMessage>
            Please complete all required fields before submitting.
          </ErrorMessage>
        )}
        <Button
          onPress={onSubmit}
          className="flex items-center justify-center rounded bg-cantelope px-6 py-3 font-bold text-soil ring-offset-4 transition-all hover:bg-soil hover:text-cantelope hover:outline-soil focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-cantelope disabled:pointer-events-none disabled:opacity-50"
          isDisabled={submissionStatus === "submitting" || hasInvalidFields}
          aria-label={
            hasInvalidFields
              ? "Submit form (disabled due to errors)"
              : "Submit form"
          }
        >
          {submissionStatus === "submitting" ? (
            <>
              <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
              Submitting...
            </>
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </div>
  );
};

export default ReviewScreen;
